/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Batch Payment Post History Button
 */
import { useMemo, useState } from 'react';
import _ from 'lodash';
import { Button, ButtonProps, Card, Divider, Empty, Flex, Popover } from 'antd';
import { CheckOne, LoadingFour, Download } from '@icon-park/react';
import { IIconProps } from '@icon-park/react/lib/runtime';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { withErrorBoundary } from 'react-error-boundary';

import { VARIABLES } from 'styles/theme';
import useBatchPostPaymentHistory from 'hooks/payments/useBatchPostPaymentHistory';
import { getClaimsRequestPath } from 'utils/router';
import { getTimeFromNowInPracticeZone } from 'utils/dateParser';
import {
	POST_PAYMENT_BATCH_CLAIM_PROCESSING_STATUS,
	POST_PAYMENT_BATCH_STATUS,
	POST_PAYMENT_GROUPED_STATUS,
} from 'app-constants/claims/era';

export interface IProps extends ButtonProps {
	requestParams: PatientPayment.IBatchPostPaymentHistoryRequest;
}

const DownloadBtn: React.FC<ButtonProps> = (props) => {
	return (
		<Button
			type='text'
			icon={<Download theme='outline' size={20} />}
			style={{ color: VARIABLES.ICON_COLOR }}
			{...props}
			data-testid='bph-trigger-btn'
		/>
	);
};

/**
 * Batch Payment Post History button
 * @param {IProps} props props
 * @returns {React.FC} Batch Payment Post History
 */
const BatchPaymentPostHistory: React.FC<IProps> = ({ requestParams, ...props }) => {
	const [open, setOpen] = useState(false);
	const { data, isLoading, isFetching } = useBatchPostPaymentHistory(requestParams);

	// function to get icon for status
	const getIcon = (status: PatientPayment.IPostPaymentBatchStatus) => {
		const defaultIconProps: IIconProps = {
			theme: 'filled',
			size: 16,
		};
		// todo check if icons are needed for other statuses
		switch (status) {
			case POST_PAYMENT_BATCH_STATUS.completed:
				return <CheckOne {...defaultIconProps} fill='#52C41A' />;
			case POST_PAYMENT_BATCH_STATUS.inProgress:
				return <LoadingFour {...defaultIconProps} fill='#8C8C8C' />;
			default:
				return null;
		}
	};

	// function to render item in popover
	const renderItem = (item: PatientPayment.IBatchPostPaymentHistoryResponse['data'][0]) => {
		const Icon = getIcon(item?.status);
		const successCount = _.get(item?.metadata?.counts, POST_PAYMENT_BATCH_CLAIM_PROCESSING_STATUS.success) || 0;
		const pendingCount = _.get(item?.metadata?.counts, POST_PAYMENT_BATCH_CLAIM_PROCESSING_STATUS.pending) || 0;
		const skippedCount = (_.sumBy(_.values(item?.metadata?.counts || {})) || 0) - successCount - pendingCount;
		// if status is completed or partial completed and there are success or skipped claims, show count section
		const showCountSection =
			(item?.status === POST_PAYMENT_BATCH_STATUS.completed || item?.status === POST_PAYMENT_BATCH_STATUS.partialCompleted) &&
			(!!successCount || !!skippedCount);

		const ItemHeader = (
			<Flex gap={16} justify='space-between'>
				<Flex align='center' gap={8}>
					{Icon} <span className='font-medium'>{'Post Payment - Batch'}</span>
				</Flex>
				{showCountSection && (
					<Flex align='center' gap={8}>
						{!!successCount && (
							<Link
								to={getClaimsRequestPath({
									batchPostPaymentMeta: {
										status: POST_PAYMENT_GROUPED_STATUS.success.value,
										batchId: item?.batchId,
										completedTimestamp: item?.completionTime,
									},
								})}
								onClick={() => setOpen(false)}
								data-testid='bph-success-link'
							>
								<u>{`Success: ${successCount} `}</u>
							</Link>
						)}
						{!!skippedCount && (
							<Link
								to={getClaimsRequestPath({
									batchPostPaymentMeta: {
										status: POST_PAYMENT_GROUPED_STATUS.skipped.value,
										batchId: item?.batchId,
										completedTimestamp: item?.completionTime,
									},
								})}
								onClick={() => setOpen(false)}
								data-testid='bph-skipped-link'
							>
								<u>{`Skipped: ${skippedCount} `}</u>
							</Link>
						)}
					</Flex>
				)}
			</Flex>
		);
		// get created and completed times
		const createdTime = getTimeFromNowInPracticeZone(item?.created);
		const completedTime = getTimeFromNowInPracticeZone(item?.completionTime);
		let ItemContent = null;
		const StartTime = <span className='text-neutral-7'>{`Started ${createdTime}`}</span>;
		if (item?.status === POST_PAYMENT_BATCH_STATUS.inProgress) {
			const processedCount = item?.acceptedClaims || 0 - pendingCount;
			ItemContent = (
				<Flex vertical>
					{StartTime} <span className='text-neutral-7'>{`${processedCount} of ${item?.acceptedClaims} processed`}</span>
				</Flex>
			);
		} else {
			if (!_.isEmpty(item?.completionTime)) {
				ItemContent = <span className='text-neutral-7'>{`Completed ${completedTime}`}</span>;
			} else {
				ItemContent = StartTime;
			}
		}
		return (
			<Flex vertical gap={8} data-testid={`bph-item-status-${item?.status}`}>
				{ItemHeader}
				<div className='pl-6.5'>{ItemContent}</div>
			</Flex>
		);
	};

	// function to render popover content
	const content = useMemo(() => {
		if (_.isEmpty(data?.data)) {
			return (
				<Card variant='borderless' loading={isLoading || isFetching} className='h-50 items-center justify-center shadow-none'>
					<Empty description={'No recent activity'} styles={{ image: { height: 60 }, root: { marginTop: 30 } }} />
				</Card>
			);
		}
		return (
			<Card variant='borderless' className='shadow-none'>
				<Flex vertical>
					{_.map(data?.data, (item, idx) => {
						const isLastItem = idx === _.size(data?.data) - 1;
						return (
							<div key={`${item?.batchId || idx}-${item?.created}`}>
								<div className={clsx({ 'pb-2': !isLastItem, 'pt-2': idx > 0 })}>{renderItem(item)}</div>
								{!isLastItem && <Divider className='m-0' />}
							</div>
						);
					})}
				</Flex>
			</Card>
		);
	}, [data, isLoading, isFetching]);

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			placement='bottomRight'
			content={content}
			classNames={{ body: clsx('p-0 min-w-100 overflow-y-auto', { 'max-h-[52vh]': !_.isEmpty(data?.data) }) }}
		>
			<DownloadBtn {...props} />
		</Popover>
	);
};

export default withErrorBoundary(BatchPaymentPostHistory, {
	fallback: <DownloadBtn disabled title='Error loading batch payment post history' />,
});
